import { Button, ErrorState, Skeleton, TOUCH } from "@nymbl/nos2-rules";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarConnectRow } from "@/components/calendar/CalendarConnectRow";
import { MeetingRow } from "@/components/calendar/MeetingRow";
import {
  acceptInOrder,
  invalidateAfterAccept,
  invalidateCalendarWeek,
  useAcceptMeeting,
  useCalendarWeek,
  useDismissMeeting,
  useRefreshCalendarWeek,
  useRestoreMeeting,
  useUndoAccept,
} from "@/hooks/use-calendar";
import { splitWarnings } from "@/hooks/use-time-entries";
import {
  acceptable,
  entryIdFor,
  hoursText,
  mayHaveSaved,
  mergeHandled,
  type PanelRow,
  panelSummary,
  rowHours,
} from "@/lib/calendar/panel";
import { ApiError } from "@/lib/time/api-client";
import { newId } from "@/lib/time/new-id";
import type { CalendarAcceptResult } from "@/schemas/calendar";
import type { AssignableTask } from "@/schemas/time";

interface CalendarPanelProps {
  /** The Sunday of the week the Time page shows. */
  weekStart: string;
  /**
   * The entry id each meeting's Accept sends, kept by the Time page for as long as it is open,
   * so a retry (even after moving weeks and back) reuses it and never logs the hours twice.
   */
  entryIds: Map<string, string>;
  /** The rows the person touched, kept by the Time page so closing the sheet loses nothing. */
  memory: PanelMemory;
}

/** This week's rows the person touched (task picked, logged, dismissed), errors and busy rows. */
interface PageState {
  weekKey: string;
  rows: Map<string, PanelRow>;
  errors: Map<string, string>;
  busy: Set<string>;
}

function without(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(key);
  return next;
}

const fresh = (weekKey: string): PageState => ({
  weekKey,
  rows: new Map(),
  errors: new Map(),
  busy: new Set(),
});

/**
 * What the panel remembers across the sheet closing and opening, for as long as the Time page is
 * open: rows handled or edited this week, and meetings whose Accept failed ambiguously (their task
 * and hours stay fixed, since the entry id may already be saved with them).
 */
export function usePanelMemory(weekStart: string) {
  const [locked, setLocked] = useState<ReadonlySet<string>>(() => new Set());
  // Rows handled here stay (Logged or Dismissed, each with Undo) until the week changes or the page is left.
  const [state, setState] = useState<PageState>(() => fresh(weekStart));
  const weekRef = useRef(weekStart);
  weekRef.current = weekStart;
  return { locked, setLocked, state, setState, weekRef };
}
export type PanelMemory = ReturnType<typeof usePanelMemory>;

/**
 * The From calendar sheet's body for the shown week: Connect calendar when not connected, else
 * one row per meeting. Its own query, so a calendar failure never touches the rest of the page;
 * an impersonating admin (403) sees nothing (the Time page hides the button too).
 */
export function CalendarPanel({ weekStart, entryIds, memory }: CalendarPanelProps) {
  const queryClient = useQueryClient();
  const week = useCalendarWeek(weekStart);
  const accept = useAcceptMeeting();
  const dismiss = useDismissMeeting();
  const restore = useRestoreMeeting();
  const undoAccept = useUndoAccept();
  const refresh = useRefreshCalendarWeek();
  const [bulk, setBulk] = useState(false);

  const { locked, setLocked, state, setState, weekRef } = memory;
  const page = state.weekKey === weekStart ? state : fresh(weekStart);
  /** Applies a change for `weekKey`; dropped when the page has moved to another week since. */
  const updateFor = (weekKey: string, change: (p: PageState) => PageState) => {
    if (weekRef.current !== weekKey) return;
    setState((prev) => change(prev.weekKey === weekKey ? prev : fresh(weekKey)));
  };
  const setRow = (weekKey: string, row: PanelRow, busy: boolean, error?: string | null) =>
    updateFor(weekKey, (p) => {
      const key = row.meeting.key;
      const rows = new Map(p.rows).set(key, row);
      const errors = new Map(p.errors);
      if (error === null) errors.delete(key);
      else if (error !== undefined) errors.set(key, error);
      const busySet = new Set(p.busy);
      if (busy) busySet.add(key);
      else busySet.delete(key);
      return { ...p, rows, errors, busy: busySet };
    });

  if (week.isError) {
    if (week.error instanceof ApiError && week.error.status === 403) return null;
    return (
      <ErrorState
        message="Calendar didn't load"
        className="py-6"
        onRetry={() => {
          void week.refetch();
        }}
      />
    );
  }
  if (week.isPending) {
    return <Skeleton className="h-12 w-full" />;
  }
  if (week.data.connection !== "connected") {
    return <CalendarConnectRow connection={week.data.connection} />;
  }

  const rows = mergeHandled(week.data.meetings, page.rows);
  const summary = panelSummary(rows);
  const matched = acceptable(rows);
  const skipped = week.data.skipped;

  /** Sends one Accept; the row shows Logged on success and keeps the server's text on failure. */
  async function acceptRow(weekKey: string, row: PanelRow): Promise<CalendarAcceptResult> {
    const task = row.chosen;
    if (!task) throw new Error("Pick a task");
    const hours = rowHours(row);
    if (hours === null) throw new Error("Check the hours");
    const key = row.meeting.key;
    setRow(weekKey, row, true, null);
    try {
      const result = await accept.mutateAsync({
        week: weekKey,
        eventKey: key,
        entryId: entryIdFor(entryIds, key, newId),
        projectTaskId: task.projectTaskId,
        demandPersonId: task.demandPersonId,
        hours,
      });
      setRow(weekKey, { ...row, state: { kind: "logged" } }, false, null);
      setLocked((prev) => without(prev, key));
      return result;
    } catch (err) {
      if (mayHaveSaved(err)) setLocked((prev) => new Set(prev).add(key));
      setRow(weekKey, row, false, err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  async function onAccept(row: PanelRow) {
    const weekKey = weekStart;
    try {
      const result = await acceptRow(weekKey, row);
      toast.success("Logged", { id: "calendar-logged" });
      for (const warning of splitWarnings({ id: result.entryId, warnings: result.warnings })
        .warnings) {
        toast.warning(warning);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      await invalidateAfterAccept(queryClient, weekKey);
    }
  }

  async function onAcceptMatched() {
    const weekKey = weekStart;
    setBulk(true);
    try {
      const results = await acceptInOrder(matched, (row) => acceptRow(weekKey, row));
      const logged = results.filter((r) => r.ok);
      const failed = results.length - logged.length;
      if (logged.length > 0) {
        toast.success(`Logged ${logged.length} ${logged.length === 1 ? "meeting" : "meetings"}`);
      }
      for (const r of logged) {
        for (const warning of splitWarnings({ id: r.result.entryId, warnings: r.result.warnings })
          .warnings) {
          toast.warning(warning);
        }
      }
      if (failed > 0) {
        toast.error(`${failed} ${failed === 1 ? "meeting" : "meetings"} didn't log`);
      }
    } finally {
      setBulk(false);
      await invalidateAfterAccept(queryClient, weekKey);
    }
  }

  async function onDismiss(row: PanelRow) {
    const weekKey = weekStart;
    setRow(weekKey, row, true);
    try {
      await dismiss.mutateAsync({ week: weekKey, eventKey: row.meeting.key });
      setRow(weekKey, { ...row, state: { kind: "dismissed" } }, false, null);
      await invalidateCalendarWeek(queryClient, weekKey);
    } catch {
      setRow(weekKey, row, false);
    }
  }

  async function onUndo(row: PanelRow) {
    const weekKey = weekStart;
    setRow(weekKey, row, true);
    try {
      await restore.mutateAsync({ eventKey: row.meeting.key });
      // Re-read first: the server lists the meeting again before the row turns back to pending.
      await invalidateCalendarWeek(queryClient, weekKey);
      setRow(weekKey, { ...row, state: { kind: "pending" } }, false, null);
    } catch {
      setRow(weekKey, row, false);
    }
  }

  async function onUndoAccept(row: PanelRow) {
    const weekKey = weekStart;
    const key = row.meeting.key;
    setRow(weekKey, row, true);
    try {
      await undoAccept.mutateAsync({ eventKey: key });
      // That entry is gone: Accepting again must make a new one, not answer "Already saved".
      entryIds.delete(key);
      // Re-read first, as onUndo does; the grid drops the hours in the same step.
      await invalidateAfterAccept(queryClient, weekKey);
      setRow(weekKey, { ...row, state: { kind: "pending" } }, false, null);
    } catch (err) {
      setRow(weekKey, row, false, err instanceof Error ? err.message : String(err));
      await invalidateAfterAccept(queryClient, weekKey);
    }
  }

  const onPick = (row: PanelRow, task: AssignableTask) =>
    setRow(weekStart, { ...row, chosen: task }, false, null);
  const onHours = (row: PanelRow, hours: string) =>
    setRow(weekStart, { ...row, hours }, false, null);

  const refreshButton = (
    <Button
      variant="outline"
      className={TOUCH}
      disabled={refresh.isPending || bulk}
      onClick={() => refresh.mutate(weekStart)}
    >
      <RefreshCw
        className={refresh.isPending ? "size-4 animate-spin" : "size-4"}
        aria-hidden="true"
      />
      {refresh.isPending ? "Refreshing…" : "Refresh"}
    </Button>
  );

  if (rows.length === 0) {
    return (
      <section aria-label="From your calendar" className="space-y-3 py-6 text-center">
        <p className="font-semibold">No meetings to log this week</p>
        <div className="flex justify-center">{refreshButton}</div>
        {skipped > 0 && (
          <p className="text-xs text-muted-foreground">
            {skipped} skipped: declined, free, all-day or private
          </p>
        )}
      </section>
    );
  }

  return (
    <section aria-label="From your calendar" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {summary.count > 0 ? (
          <p className="text-sm text-muted-foreground">
            {summary.count} {summary.count === 1 ? "meeting" : "meetings"} ·{" "}
            {hoursText(summary.hours)}
          </p>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap items-center gap-2">
          {refreshButton}
          {summary.count > 0 && (
            <Button
              className={TOUCH}
              disabled={matched.length === 0 || bulk}
              onClick={() => {
                void onAcceptMatched();
              }}
            >
              Accept {matched.length} matched
            </Button>
          )}
        </div>
      </div>
      <ul className="divide-y max-sm:space-y-2 max-sm:divide-y-0">
        {rows.map((row, index) => (
          <MeetingRow
            key={row.meeting.key}
            row={row}
            index={index}
            busy={bulk || page.busy.has(row.meeting.key)}
            error={page.errors.get(row.meeting.key) ?? null}
            locked={locked.has(row.meeting.key)}
            onPick={(task) => onPick(row, task)}
            onHours={(hours) => onHours(row, hours)}
            onAccept={() => {
              void onAccept(row);
            }}
            onDismiss={() => {
              void onDismiss(row);
            }}
            onUndo={() => {
              void onUndo(row);
            }}
            onUndoAccept={() => {
              void onUndoAccept(row);
            }}
          />
        ))}
      </ul>
      {skipped > 0 && (
        <p className="text-xs text-muted-foreground">
          {skipped} skipped: declined, free, all-day or private
        </p>
      )}
    </section>
  );
}
